import { cache } from 'react'
import { marked } from 'marked'
import { getApiUrl } from '@/lib/api'
import { serviceUrlSegment } from '@/lib/slugify-service-title'

/** Strapi REST media → absolute URL (call getApiUrl() per use so client/server stay in sync). */
export function strapiMediaUrl(relativeOrAbsolute: string | null | undefined): string | null {
  if (!relativeOrAbsolute || typeof relativeOrAbsolute !== 'string') return null
  const u = relativeOrAbsolute.trim()
  if (!u) return null
  if (u.startsWith('http://') || u.startsWith('https://')) return u
  const base = getApiUrl().replace(/\/$/, '')
  const path = u.startsWith('/') ? u : `/${u}`
  return `${base}${path}`
}

/** Strapi Upload file object (REST v4 `attributes`, v5 flat, or `{ data }`) → usable absolute URL. */
export function pickBestUrlFromStrapiFile(file: unknown): string | null {
  if (file == null) return null
  let node: unknown = file
  if (typeof node === 'object' && node !== null && 'data' in node) {
    const d = (node as { data: unknown }).data
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      node = (d as { attributes?: unknown }).attributes ?? d
    } else if (Array.isArray(d) && d[0] && typeof d[0] === 'object') {
      node = d[0]
      if (typeof node === 'object' && node !== null && 'attributes' in node) {
        node = (node as { attributes: unknown }).attributes
      }
    }
  }
  if (!node || typeof node !== 'object') return null
  const o = node as Record<string, unknown>
  // Prefer the canonical upload URL. `next/image` resizes and re-encodes on its
  // own; Strapi's `medium_` / `large_` / `.avif` derivatives are redundant and
  // some pre-converted variants make the optimizer return 400 Bad Request.
  if (typeof o.url === 'string' && o.url.length > 0) {
    const abs = strapiMediaUrl(o.url)
    if (abs) return abs
  }
  const formats = o.formats as Record<string, { url?: string }> | undefined
  const order = ['large', 'medium', 'small', 'thumbnail'] as const
  for (const k of order) {
    const u = formats?.[k]?.url
    if (typeof u === 'string' && u.length > 0) {
      const abs = strapiMediaUrl(u)
      if (abs) return abs
    }
  }
  const hash = o.hash
  const ext = o.ext
  if (typeof hash === 'string' && typeof ext === 'string') {
    const e = ext.startsWith('.') ? ext : `.${ext}`
    return strapiMediaUrl(`/uploads/${hash}${e}`)
  }
  return null
}

function mimeFromStrapiFile(file: unknown): string {
  if (!file || typeof file !== 'object') return ''
  const o = file as Record<string, unknown>
  if (typeof o.mime === 'string') return o.mime
  const ext = String(o.ext ?? '').toLowerCase().replace(/^\./, '')
  if (['mp4', 'webm', 'ogg'].includes(ext)) return `video/${ext === 'mp4' ? 'mp4' : ext}`
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    return `image/${ext === 'jpg' ? 'jpeg' : ext}`
  }
  return ''
}

/**
 * CMS HTML often contains `<img src="/uploads/...">`; the browser would load that from the
 * Next.js origin and 404. Rewrite those src values to the Strapi base URL.
 */
function isRelativeStrapiUploadPath(src: string): boolean {
  const s = src.trim()
  return (
    s.startsWith('/uploads/') ||
    /^uploads\//i.test(s) ||
    (!s.startsWith('http') && !s.startsWith('data:') && !s.startsWith('blob:') && s.includes('/uploads/'))
  )
}

export function rewriteHtmlExternalLinksToNewTab(html: string): string {
  return html.replace(
    /<a\b([^>]*)\bhref=(["'])(https?:\/\/[^"']+)\2([^>]*)>/gi,
    (_match, before: string, quote: string, href: string, after: string) => {
      const attrs = `${before}${after}`
      if (/\btarget\s*=/i.test(attrs)) return _match
      return `<a${before} href=${quote}${href}${quote}${after} target="_blank" rel="noopener noreferrer">`
    },
  )
}

export function rewriteHtmlUploadSrcToAbsolute(html: string): string {
  if (!html.includes('uploads')) return html
  const base = getApiUrl().replace(/\/$/, '')
  return html.replace(
    /(<img\b[^>]*\bsrc=)(["'])([^"']+)\2/gi,
    (match, prefix: string, quote: string, src: string) => {
      const s = String(src).trim()
      if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:') || s.startsWith('blob:')) {
        return match
      }
      if (!isRelativeStrapiUploadPath(s)) return match
      const path = s.startsWith('/') ? s : `/${s}`
      return `${prefix}${quote}${base}${path}${quote}`
    }
  )
}

type MediaItem = { url: string; mime: string }

/** Strapi v4/v5: repeatable media → array of items; `data` may be one object or an array. */
function unwrapRelationData(raw: unknown): unknown[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  const d = (raw as { data?: unknown }).data
  if (d == null) return []
  if (Array.isArray(d)) return d
  if (typeof d === 'object') return [d]
  return []
}

/** Strapi v4/v5: media can be { data: [] }, flat [], or nested attributes. */
function extractMediaList(raw: unknown): MediaItem[] {
  const maybeArr = unwrapRelationData(raw)
  const out: MediaItem[] = []
  for (const item of maybeArr) {
    const merged = (item as { attributes?: Record<string, unknown> }).attributes
      ? {
          ...(item as { attributes: Record<string, unknown> }).attributes,
          id: (item as { id?: number }).id,
        }
      : item
    const url = pickBestUrlFromStrapiFile(merged)
    if (!url) continue
    const mime = mimeFromStrapiFile(merged)
    out.push({ url, mime })
  }
  return out
}

function splitGalleryByMime(items: MediaItem[]): { imageUrls: string[]; videoUrls: string[] } {
  const imageUrls: string[] = []
  const videoUrls: string[] = []
  for (const { url, mime } of items) {
    const m = mime.toLowerCase()
    if (m.startsWith('video/')) videoUrls.push(url)
    else imageUrls.push(url)
  }
  return { imageUrls, videoUrls }
}

function parseSingleMedia(raw: unknown): { url: string | null; name: string | null } {
  if (raw == null) return { url: null, name: null }
  let item: unknown
  if (typeof raw === 'object' && raw !== null && 'data' in raw) {
    const d = (raw as { data: unknown }).data
    if (d == null || (Array.isArray(d) && d.length === 0)) return { url: null, name: null }
    item = Array.isArray(d) ? d[0] : d
  } else {
    item = raw
  }
  if (!item || typeof item !== 'object') return { url: null, name: null }
  const flat = (item as { attributes?: Record<string, unknown> }).attributes
    ? { ...(item as { attributes: Record<string, unknown> }).attributes }
    : (item as Record<string, unknown>)
  const name =
    (flat.name as string | undefined) ||
    (flat.alternativeText as string | undefined) ||
    null
  return { url: pickBestUrlFromStrapiFile(flat), name }
}

function plainTextFromHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

/** Strapi 5 blocks tree → plain string (cards, meta). */
function blocksToPlainText(node: unknown): string {
  if (node == null) return ''
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(blocksToPlainText).filter(Boolean).join(' ')
  if (typeof node === 'object') {
    const o = node as Record<string, unknown>
    if (typeof o.text === 'string') return o.text
    if (o.children != null) return blocksToPlainText(o.children)
  }
  return ''
}

/** Strapi richtext: HTML string, legacy `{ body }`, or Strapi 5 blocks `[]`. */
function parseRichtextHtmlOnly(raw: unknown): string | null {
  if (raw == null) return null
  if (typeof raw === 'string') return raw.trim() ? raw : null
  if (
    typeof raw === 'object' &&
    raw !== null &&
    !Array.isArray(raw) &&
    'body' in raw &&
    typeof (raw as { body: unknown }).body === 'string'
  ) {
    const b = (raw as { body: string }).body
    return b.trim() ? b : null
  }
  return null
}

/**
 * Strapi "Rich text (Markdown)" stores raw CommonMark — not HTML.
 * Avoid treating real HTML from other editor modes as markdown.
 */
function looksLikeMarkdown(s: string): boolean {
  const t = s.trim()
  if (!t) return false
  if (/^<[a-z!]/i.test(t)) {
    if (
      /<\/[a-z][\w:]*>/i.test(t) ||
      /<br\s*\/?>/i.test(t) ||
      /<p[\s>]/i.test(t) ||
      /<h[1-6][\s>]/i.test(t) ||
      /<ul[\s>]/i.test(t) ||
      /<ol[\s>]/i.test(t)
    ) {
      return false
    }
  }
  if (/^#{1,6}\s+\S/m.test(t)) return true
  if (/^\s*[-*+]\s+\S/m.test(t)) return true
  if (/\[[^\]]+\]\([^)]+\)/.test(t)) return true
  if (/^>\s+/m.test(t)) return true
  if (/^```/m.test(t)) return true
  if (/^\*\*[^*]/.test(t) || /^_[^_]/.test(t)) return true
  return false
}

function markdownToHtml(md: string): string {
  return marked.parse(md, { async: false }) as string
}

/** Top-level nodes Strapi Blocks editor sends to the REST API. */
const STRAPI_ROOT_BLOCK_TYPES = new Set([
  'paragraph',
  'heading',
  'list',
  'quote',
  'code',
  'image',
])

function isRootBlocksArray(x: unknown): x is unknown[] {
  if (!Array.isArray(x) || x.length === 0) return false
  return x.every((item) => {
    if (!item || typeof item !== 'object') return false
    const t = (item as { type?: unknown }).type
    return typeof t === 'string' && STRAPI_ROOT_BLOCK_TYPES.has(t)
  })
}

/** True if `raw` is a Strapi Blocks root array (for `@strapi/blocks-react-renderer`). */
export function isStrapiBlocksRootContent(raw: unknown): boolean {
  return isRootBlocksArray(raw)
}

/**
 * Strapi 5 Blocks: array, `{ type: 'doc', children }`, or JSON string of the same.
 * Must not treat other arrays (e.g. repeatable components) as blocks.
 */
function extractBlocksFromRichtext(raw: unknown): unknown[] | null {
  if (raw == null) return null

  if (typeof raw === 'string') {
    const t = raw.trim()
    if (!t || (!t.startsWith('[') && !t.startsWith('{'))) return null
    try {
      const p = JSON.parse(t) as unknown
      if (isRootBlocksArray(p)) return p
      if (
        p &&
        typeof p === 'object' &&
        !Array.isArray(p) &&
        (p as { type?: string }).type === 'doc' &&
        Array.isArray((p as { children?: unknown }).children) &&
        isRootBlocksArray((p as { children: unknown[] }).children)
      ) {
        return (p as { children: unknown[] }).children
      }
    } catch {
      return null
    }
    return null
  }

  if (isRootBlocksArray(raw)) return raw

  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>
    if (
      o.type === 'doc' &&
      Array.isArray(o.children) &&
      isRootBlocksArray(o.children)
    ) {
      return o.children
    }
  }

  return null
}

function parseRichtextParts(raw: unknown): {
  html: string | null
  blocks: unknown[] | null
  excerpt: string
} {
  const blocks = extractBlocksFromRichtext(raw)
  let html = blocks ? null : parseRichtextHtmlOnly(raw)
  if (html && looksLikeMarkdown(html)) {
    try {
      html = markdownToHtml(html)
    } catch {
      /* keep original string */
    }
  }
  const excerpt = blocks?.length
    ? blocksToPlainText(blocks).replace(/\s+/g, ' ').trim().slice(0, 900)
    : html
      ? plainTextFromHtml(html).slice(0, 900)
      : blocksToPlainText(raw).replace(/\s+/g, ' ').trim().slice(0, 900)
  return { html, blocks, excerpt }
}

/** Full path `/usluge/<segment>` from title (+ id fallback). */
export function serviceDetailPath(service: Pick<MarketplaceService, 'title' | 'id'>): string {
  return `/usluge/${serviceUrlSegment(service)}`
}

/** Deduped image URLs for card / preview carousels (gallery first, else hero). */
export function serviceCarouselImageUrls(s: MarketplaceService): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const u of s.galleryUrls) {
    if (u && !seen.has(u)) {
      seen.add(u)
      out.push(u)
    }
  }
  if (out.length === 0 && s.heroImageUrl) return [s.heroImageUrl]
  return out
}

export type ServiceCategoryRef = {
  id: number
  documentId?: string
  name: string
  slug?: string | null
}

export type ServiceLocationRef = {
  id: number
  documentId?: string
  locationName?: string | null
  city: string
  address?: string | null
  latitude: number
  longitude: number
}

export type ServiceAddressItem = {
  id?: number
  addressTitle: string
  addressMapUrl?: string | null
}

export type ServicePayment = {
  id?: number
  date: string        // ISO date string YYYY-MM-DD
  amount: number
  svrhaPlacanja: string
  paid: boolean
}

export type MarketplaceService = {
  id: number
  documentId?: string
  title: string
  /** URL segment from slugified title (`usluga-<id>` if title cannot be slugged). Not from Strapi. */
  slug: string
  /** Strapi `addresses` richtext */
  addressesHtml: string | null
  addressesBlocks: unknown[] | null
  addressesExcerpt: string
  /** Strapi `description` richtext (separate from addresses). */
  descriptionHtml: string | null
  descriptionBlocks: unknown[] | null
  descriptionExcerpt: string
  /**
   * Combined “summary” for cards: `description` field first, then `addresses`.
   * @deprecated Prefer `descriptionHtml` / `addressesHtml` explicitly.
   */
  description: string | null
  /** Strapi `websiteURL` */
  websiteUrl?: string | null
  /** Strapi `highlighted` */
  highlighted: boolean
  /** Strapi `toggleService` — when true, shown on homepage grid and in chat catalog. */
  toggleService: boolean
  heroImageUrl?: string | null
  galleryUrls: string[]
  /** All uploaded / legacy video URLs (gallery videos + optional `videoUrl`). */
  videoUrls: string[]
  /** First video URL for simple “play” entry points. */
  videoUrl?: string | null
  contactEmail?: string | null
  /** All phone numbers from the `phoneNumbers` repeatable component. */
  phoneNumbers: { phoneNumber: string; label?: string | null }[]
  instagramUrl?: string | null
  facebookUrl?: string | null
  whatsappNumber?: string | null
  viberNumber?: string | null
  /** Optional downloadable file from Strapi `additionalOptionalFile`. */
  additionalOptionalFileUrl?: string | null
  additionalOptionalFileName?: string | null
  /** Custom display label for the file link (Strapi `additionalOptionalFileLabel` text field). */
  additionalOptionalFileLabel?: string | null
  /** Button text for the file download button (Strapi `additionalOptionalFileButtonText` text field). */
  additionalOptionalFileButtonText?: string | null
  serviceAddresses: ServiceAddressItem[]
  categories: ServiceCategoryRef[]
  locations: ServiceLocationRef[]
  payments: ServicePayment[]
}

function parseGalleryUrls(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((x): x is string => typeof x === 'string' && x.length > 0)
  }
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown
      if (Array.isArray(p)) {
        return p.filter((x): x is string => typeof x === 'string' && x.length > 0)
      }
    } catch {
      return []
    }
  }
  return []
}

function normalizeCategory(raw: any): ServiceCategoryRef {
  const o = raw?.attributes ? { id: raw.id, documentId: raw.documentId, ...raw.attributes } : raw
  return {
    id: o.id,
    documentId: o.documentId,
    name: o.name ?? '',
    slug: o.slug ?? null,
  }
}

function normalizeLocation(raw: any): ServiceLocationRef {
  const o = raw?.attributes ? { id: raw.id, documentId: raw.documentId, ...raw.attributes } : raw
  const lat = Number(o.latitude)
  const lng = Number(o.longitude)
  return {
    id: o.id,
    documentId: o.documentId,
    locationName: o.locationName ?? null,
    city: o.city ?? '',
    address: o.address ?? null,
    latitude: Number.isFinite(lat) ? lat : 0,
    longitude: Number.isFinite(lng) ? lng : 0,
  }
}

function normalizePayments(raw: unknown): ServicePayment[] {
  const rows = unwrapRelationData(raw)
  if (!Array.isArray(rows) || rows.length === 0) return []
  const out: ServicePayment[] = []
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const o = (row as { attributes?: Record<string, unknown> }).attributes
      ? { ...(row as { attributes: Record<string, unknown> }).attributes, id: (row as { id?: number }).id }
      : (row as Record<string, unknown>)
    const date = typeof o.date === 'string' ? o.date : ''
    const amount = typeof o.amount === 'number' ? o.amount : parseFloat(String(o.amount ?? 0)) || 0
    const svrhaPlacanja = typeof o.svrhaPlacanja === 'string' ? o.svrhaPlacanja.trim() : ''
    const paid = Boolean(o.paid)
    out.push({ id: typeof o.id === 'number' ? o.id : undefined, date, amount, svrhaPlacanja, paid })
  }
  return out
}

function normalizeServiceAddresses(raw: unknown): ServiceAddressItem[] {
  const rows = unwrapRelationData(raw)
  if (!Array.isArray(rows) || rows.length === 0) return []
  const out: ServiceAddressItem[] = []
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const o = (row as { attributes?: Record<string, unknown> }).attributes
      ? { ...(row as { attributes: Record<string, unknown> }).attributes, id: (row as { id?: number }).id }
      : (row as Record<string, unknown>)
    const addressTitle = typeof o.addressTitle === 'string' ? o.addressTitle.trim() : ''
    if (!addressTitle) continue
    const rawUrl = typeof o.addressMapUrl === 'string' ? o.addressMapUrl.trim() : ''
    const addressMapUrl = rawUrl
      ? (/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl.replace(/^\/+/, '')}`)
      : null
    out.push({
      id: typeof o.id === 'number' ? o.id : undefined,
      addressTitle,
      addressMapUrl,
    })
  }
  return out
}

function normalizeService(
  raw: any,
  opts?: { includePayments?: boolean },
): MarketplaceService {
  const includePayments = opts?.includePayments === true
  const o = raw?.attributes ? { id: raw.id, documentId: raw.documentId, ...raw.attributes } : raw

  let categories: ServiceCategoryRef[] = []
  const c = o.categories
  if (Array.isArray(c)) {
    categories = c.map(normalizeCategory)
  } else if (c?.data && Array.isArray(c.data)) {
    categories = c.data.map(normalizeCategory)
  }

  let locations: ServiceLocationRef[] = []
  const l = o.locations
  if (Array.isArray(l)) {
    locations = l.map(normalizeLocation)
  } else if (l?.data && Array.isArray(l.data)) {
    locations = l.data.map(normalizeLocation)
  }

  const title = o.title ?? ''

  const galleryItems = extractMediaList(o.imageGallery)
  const split = splitGalleryByMime(galleryItems)
  let galleryUrls =
    split.imageUrls.length > 0 ? split.imageUrls : parseGalleryUrls(o.galleryUrls)
  const legacyHero =
    typeof o.heroImageUrl === 'string' ? strapiMediaUrl(o.heroImageUrl) : null
  const heroImageUrl = galleryUrls[0] ?? legacyHero ?? null

  const legacyVideo = typeof o.videoUrl === 'string' ? o.videoUrl.trim() : ''
  const videoUrls = [...split.videoUrls]
  if (legacyVideo && !videoUrls.includes(legacyVideo)) videoUrls.push(legacyVideo)
  const videoUrl = videoUrls[0] ?? null

  const desc = parseRichtextParts(o.description)
  const serviceAddresses = normalizeServiceAddresses(o.addresses)
  const payments = includePayments ? normalizePayments(o.payments) : []
  const addressTitlesExcerpt = serviceAddresses.map((a) => a.addressTitle).join(', ')

  /** `addresses` is a repeatable address component in Strapi, not richtext. */
  const addressesHtml = null
  const addressesBlocks = null
  const addressesExcerpt = addressTitlesExcerpt

  const descriptionHtml = desc.html
  const descriptionBlocks = desc.blocks
  const descriptionExcerpt = desc.excerpt

  /** Card / legacy: marketing `description` richtext only. */
  const summaryHtml = descriptionHtml

  const rawWebsite = o.websiteURL ?? o.websiteUrl
  const websiteUrl =
    typeof rawWebsite === 'string' && rawWebsite.trim()
      ? /^https?:\/\//i.test(rawWebsite.trim())
        ? rawWebsite.trim()
        : `https://${rawWebsite.trim().replace(/^\/+/, '')}`
      : null

  const optional = parseSingleMedia(o.additionalOptionalFile)

  return {
    id: o.id,
    documentId: o.documentId,
    title,
    slug: serviceUrlSegment({ title, id: o.id }),
    addressesHtml: addressesHtml ?? null,
    addressesBlocks,
    addressesExcerpt: addressesExcerpt || addressTitlesExcerpt,
    descriptionHtml: descriptionHtml ?? null,
    descriptionBlocks,
    descriptionExcerpt,
    description: summaryHtml ?? null,
    websiteUrl,
    highlighted: Boolean(o.highlighted),
    toggleService: Boolean(o.toggleService),
    heroImageUrl,
    galleryUrls,
    videoUrls,
    videoUrl: videoUrl ?? null,
    contactEmail: typeof o.contactEmail === 'string' ? o.contactEmail : null,
    phoneNumbers: Array.isArray(o.phoneNumbers)
      ? o.phoneNumbers
          .filter((p: any) => typeof p?.phoneNumber === 'string' && p.phoneNumber.trim())
          .map((p: any) => ({ phoneNumber: p.phoneNumber.trim(), label: p.label?.trim() || null }))
      : [],
    instagramUrl: typeof o.instagramUrl === 'string' ? o.instagramUrl : null,
    facebookUrl: typeof o.facebookUrl === 'string' ? o.facebookUrl : null,
    whatsappNumber: typeof o.whatsappNumber === 'string' ? o.whatsappNumber.trim() || null : null,
    viberNumber: typeof o.viberNumber === 'string' ? o.viberNumber.trim() || null : null,
    additionalOptionalFileUrl: optional.url,
    additionalOptionalFileName: optional.name,
    additionalOptionalFileLabel: typeof o.additionalOptionalFileLabel === 'string' ? o.additionalOptionalFileLabel.trim() || null : null,
    additionalOptionalFileButtonText: typeof o.additionalOptionalFileButtonText === 'string' ? o.additionalOptionalFileButtonText.trim() || null : null,
    serviceAddresses,
    categories,
    locations,
    payments,
  }
}

/*
 * Top-level scalar fields we actually consume in the UI. Asking Strapi to
 * return only these (instead of every column on the row) drops the JSON
 * payload size noticeably on the marketplace list — most services have
 * autogenerated timestamps and other fields we never read.
 *
 * Keep this list in sync with the data accessed inside `normalizeService`.
 */
const SERVICE_FIELDS = [
  'title',
  'contactEmail',
  'instagramUrl',
  'facebookUrl',
  'whatsappNumber',
  'viberNumber',
  'websiteURL',
  'highlighted',
  'toggleService',
  'description',
  'additionalOptionalFileButtonText',
] as const

/*
 * Relations + components we render. The array form (`populate[0]=…`) avoids
 * the Strapi 5 validation issue that `populate[categories]=*` triggers —
 * with the array notation Strapi only returns the relation's own fields and
 * does NOT try to walk the inverse `services` side, which both cuts response
 * size and stops the 400 we used to fall back from.
 *
 * `payments` is appended only for admin callers (`includePayments=true`).
 */
const SERVICE_POPULATE = [
  'categories',
  'locations',
  'addresses',
  'phoneNumbers',
  'imageGallery',
  'additionalOptionalFile',
] as const

function appendServicePopulate(
  params: URLSearchParams,
  includePayments: boolean,
): void {
  // Top-level field allow-list.
  SERVICE_FIELDS.forEach((field, index) => {
    params.append(`fields[${index}]`, field)
  })

  // Relations + components.
  const relations = includePayments
    ? [...SERVICE_POPULATE, 'payments']
    : [...SERVICE_POPULATE]
  relations.forEach((rel, index) => {
    params.append(`populate[${index}]`, rel)
  })
}

function buildMarketplaceListParams(includePayments: boolean): URLSearchParams {
  const params = new URLSearchParams({
    'pagination[pageSize]': '100',
    sort: 'title:asc',
  })
  appendServicePopulate(params, includePayments)
  return params
}

/*
 * Marketplace data is read on every page render (home, service detail,
 * favourites, etc.). Without an explicit `cache` hint Next 15 treats every
 * call to `fetch()` as dynamic — which means a fresh round-trip to Strapi
 * per request, plus duplicate fetches within a single render tree.
 *
 * We do two things:
 *   1. `next: { revalidate, tags }` — turns each Strapi GET into a tagged ISR
 *      entry. The shared response is reused for `revalidate` seconds across
 *      ALL incoming requests; admin mutations call
 *      `revalidateTag('marketplace-services')` to bust it instantly.
 *   2. `cache: 'force-cache'` is implied by `revalidate`, kept explicit for
 *      readers.
 *   3. Including the `Authorization` header (when a user token is in scope)
 *      would otherwise opt the response out of the data cache, but the
 *      public services endpoint is read without auth, so the cache is safe.
 *
 * Admin-only payloads (`includePayments=true`) must NOT be cached publicly —
 * we therefore opt those out with `cache: 'no-store'`.
 */
export const SERVICES_CACHE_TAG = 'marketplace-services'
const SERVICES_REVALIDATE_SECONDS = 240

async function fetchServicesFromApi(
  searchParams: URLSearchParams,
  opts?: { includePayments?: boolean },
): Promise<MarketplaceService[]> {
  const apiBase = `${getApiUrl()}/api/services`
  const url = `${apiBase}?${searchParams.toString()}`

  const fetchInit: RequestInit = opts?.includePayments
    ? {
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
      }
    : {
        headers: { 'Content-Type': 'application/json' },
        next: {
          revalidate: SERVICES_REVALIDATE_SECONDS,
          tags: [SERVICES_CACHE_TAG],
        },
      }

  let res = await fetch(url, fetchInit)

  // Defensive fallback: if our selective `fields[…]` + `populate[…]` request
  // is rejected by a future Strapi schema mismatch, retry with the broad
  // `populate=*` form so the page still loads. This should be extremely rare
  // in practice; the fast path returns a much smaller JSON payload.
  if (!res.ok && res.status === 400) {
    const fallbackParams = new URLSearchParams({
      'pagination[pageSize]': searchParams.get('pagination[pageSize]') ?? '100',
      sort: searchParams.get('sort') ?? 'title:asc',
      populate: '*',
    })
    // Preserve numeric-id filter if it was set on the original request.
    const idFilter = searchParams.get('filters[id][$eq]')
    if (idFilter) fallbackParams.set('filters[id][$eq]', idFilter)
    res = await fetch(`${apiBase}?${fallbackParams.toString()}`, fetchInit)
  }

  if (!res.ok) {
    const t = await res.text()
    console.error('fetchServicesFromApi', res.status, t)
    throw new Error('Nije moguće učitati usluge.')
  }
  const json = await res.json()
  const rows = json.data
  if (!Array.isArray(rows)) return []
  return rows.map((row) => normalizeService(row, opts))
}

/** One service by numeric `id` (matches `/usluge/usluga-123` fallback URLs). Full `populate=*` payload. */
export const fetchServiceByNumericId = cache(
  async (
    id: number,
    options?: { includePayments?: boolean },
  ): Promise<MarketplaceService | null> => {
    const params = new URLSearchParams({
      'filters[id][$eq]': String(id),
    })
    appendServicePopulate(params, options?.includePayments === true)
    try {
      const list = await fetchServicesFromApi(params, options)
      return list[0] ?? null
    } catch {
      return null
    }
  },
)

/**
 * Fetch published services with categories and locations (for filters + cards + map).
 * Payment rows are omitted from the normalized object unless `includePayments: true` (admin only).
 *
 * Wrapped in React `cache()` so multiple components on the same server render
 * (e.g. metadata generator + page body + sitemap) share one underlying Strapi
 * fetch. Combined with the `next.tags + revalidate` cache below, this collapses
 * what used to be N requests-per-render down to a single hot hit.
 */
export const fetchMarketplaceServices = cache(
  async (options?: { includePayments?: boolean }): Promise<MarketplaceService[]> => {
    const includePayments = options?.includePayments === true
    return fetchServicesFromApi(buildMarketplaceListParams(includePayments), {
      includePayments,
    })
  },
)

const SITEMAP_SERVICES_PAGE_SIZE = 100

/** All published services for sitemap (paginates past Strapi's default page size). */
export const fetchAllMarketplaceServices = cache(
  async (): Promise<MarketplaceService[]> => {
    const all: MarketplaceService[] = []
    let page = 1
    for (;;) {
      const params = buildMarketplaceListParams(false)
      params.set('pagination[pageSize]', String(SITEMAP_SERVICES_PAGE_SIZE))
      params.set('pagination[page]', String(page))
      const batch = await fetchServicesFromApi(params)
      all.push(...batch)
      if (batch.length < SITEMAP_SERVICES_PAGE_SIZE) break
      page += 1
    }
    return all
  },
)

/**
 * Resolve detail page by URL segment (slugified title or `usluga-<id>` fallback).
 * Strapi no longer stores `slug` on services.
 *
 * Cached per-request: `app/usluge/[slug]/page.tsx` calls this from both
 * `generateMetadata` and the page body — without `cache()` they would each
 * hit Strapi independently.
 */
export const fetchServiceBySlug = cache(
  async (segment: string): Promise<MarketplaceService | null> => {
    try {
      const raw = decodeURIComponent(segment).toLowerCase()
      const idMatch = /^usluga-(\d+)$/.exec(raw)
      if (idMatch) {
        const id = Number(idMatch[1])
        if (Number.isFinite(id)) {
          const direct = await fetchServiceByNumericId(id)
          if (direct) return direct
        }
      }
      const all = await fetchMarketplaceServices()
      if (idMatch) {
        const id = Number(idMatch[1])
        if (Number.isFinite(id)) return all.find((s) => s.id === id) ?? null
      }
      return all.find((s) => serviceUrlSegment(s) === raw) ?? null
    } catch (e) {
      console.error('fetchServiceBySlug', e)
      return null
    }
  },
)

export type ServiceFilters = {
  categoryIds: number[]
  locationIds: number[]
  highlightedOnly?: boolean
}

/** Homepage marketplace grid: only services with `toggleService === true`. */
export function filterServicesForCatalogGrid(
  services: MarketplaceService[],
): MarketplaceService[] {
  return services.filter((s) => s.toggleService)
}

/** Istaknute usluge uvijek na vrhu liste (zadržava relativni red unutar grupe). */
export function sortServicesHighlightedFirst(
  services: MarketplaceService[],
): MarketplaceService[] {
  return [...services].sort((a, b) => {
    if (a.highlighted === b.highlighted) {
      return a.title.localeCompare(b.title, 'sr')
    }
    return a.highlighted ? -1 : 1
  })
}

/** Client-side AND filter: must match at least one selected category (if any) and at least one selected location (if any). */
export function filterServices(
  services: MarketplaceService[],
  filters: ServiceFilters
): MarketplaceService[] {
  const { categoryIds, locationIds, highlightedOnly = false } = filters
  const catSet = new Set(categoryIds)
  const locSet = new Set(locationIds)

  return services.filter((s) => {
    if (highlightedOnly && !s.highlighted) return false
    if (catSet.size > 0) {
      const ok = s.categories.some((c) => catSet.has(c.id))
      if (!ok) return false
    }
    if (locSet.size > 0) {
      const ok = s.locations.some((l) => locSet.has(l.id))
      if (!ok) return false
    }
    return true
  })
}

/** Unique locations from a list of services (for the map). */
export function collectLocationsForMap(services: MarketplaceService[]): ServiceLocationRef[] {
  const byId = new Map<number, ServiceLocationRef>()
  for (const s of services) {
    for (const l of s.locations) {
      if (!byId.has(l.id)) byId.set(l.id, l)
    }
  }
  return Array.from(byId.values())
}

export function serviceFavoriteKey(s: Pick<MarketplaceService, 'id' | 'documentId'>): string {
  return s.documentId ? `d:${s.documentId}` : `i:${s.id}`
}

export function parseFavoriteKey(key: string): { id?: number; documentId?: string } {
  if (key.startsWith('d:')) return { documentId: key.slice(2) }
  if (key.startsWith('i:')) return { id: Number(key.slice(2)) }
  return { id: Number(key) }
}
